import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import ReactDOM from "react-dom";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import api from "../../utils/api";
import iconSegure from "../../assets/Frame 20 (9).png";
import LoadingIcon from "../../components/LoadingIcon";
import Benefits from "./components/PaymentPlan/Benefits";
import StripeWrapper from "../../utils/StripeWrapper";
import "./PaymentCheckoutModal.css";
import { getPeriodLabel, formatCurrency } from "../../utils/period";
import arrowRight from "../../assets/icons/arrowRight.svg";
import arrowLeft from "../../assets/icons/arrowLeft.svg";

const ModalHeader = ({ onClose, isLoading }) => (
  <div className="modalHeader">
    <button
      className="closeButton"
      onClick={!isLoading ? onClose : undefined}
      disabled={isLoading}
    >
      ×
    </button>
  </div>
);

const PlanSection = ({ currentPlan, currentPrice, onPeriodChange, isLoading, autoRenew, setAutoRenew }) => (
  <div className="planSection">
    <div className="planDescription">
      Plano escolhido: <span className="planTitle">{currentPlan?.name}</span>
    </div>

    {/* Period selector */}
    {currentPlan?.prices && currentPlan.prices.length > 1 && (
      <div className="periodSelector">
        {currentPlan.prices
          .filter(price => price.active)
          .map(price => (
            <button
              key={price.period}
              className={`periodButton ${currentPrice?.period === price.period ? 'active' : ''}`}
              onClick={() => onPeriodChange(price.period)}
              disabled={isLoading}
            >
              {getPeriodLabel(price.period)}
            </button>
          ))}
      </div>
    )}

    <div className="planPrice">
      {currentPrice?.hasSavings && currentPrice?.totalSavings > 0 && (
        <div className="savingsBadge">
          Economia de R$ {formatCurrency(parseFloat(currentPrice.totalSavings))}
        </div>
      )}

      {currentPrice?.hasSavings && currentPrice?.originalPrice && (
        <div className="originalPrice">
          R$ {formatCurrency(parseFloat(currentPrice.originalPrice))} / mês
        </div>
      )}

      <div className="planPriceContainer">
        <span className="planPriceDescription">R$</span>
        <h1 className="planAmount">
          {currentPrice?.amount
            ? formatCurrency(currentPrice.amount).replace('R$', '').trim()
            : '0,00'
          }
        </h1>
        <div className="planPriceDescription">
          / mês
        </div>
      </div>
    </div>

    <Benefits benefits={currentPlan?.benefits} />

    {/* Auto renew switch */}
    <div className="autoRenewSwitchContainer">
      <label className="autoRenewSwitchLabel">
        <span>Renovação automática</span>
        <div className="autoRenewSwitch">
          <input
            type="checkbox"
            checked={autoRenew}
            onChange={(e) => setAutoRenew(e.target.checked)}
            disabled={isLoading}
          />
          <span className="autoRenewSlider"></span>
        </div>
      </label>
    </div>
  </div>
);

const formatBillingCycle = (cycle) => {
  const cycleMap = {
    monthly: "mês",
    quarterly: "trimestre",
    semiannual: "semestre",
    annual: "ano",
  };
  return cycleMap[cycle] || cycle;
};

// Hook for the plan logic
const usePlanLogic = (externalPlans, selectedPlan, selectedPrice, currentPlanId) => {
  const [plans, setPlans] = useState([]);
  const [currentPlanIndex, setCurrentPlanIndex] = useState(0);
  const [currentPrice, setCurrentPrice] = useState(selectedPrice);
  const [currentPlan, setCurrentPlan] = useState(selectedPlan);

  useEffect(() => {
    if (externalPlans && externalPlans.length > 0) {
      // Filter out the current plan
      const filteredPlans = externalPlans.filter(plan => plan.id !== currentPlanId);

      // Sort plans by value (lowest to highest)
      const sortedPlans = filteredPlans.sort((a, b) => {
        const priceA = a.prices?.find(p => p.active)?.amount || 0;
        const priceB = b.prices?.find(p => p.active)?.amount || 0;
        return priceA - priceB;
      });

      setPlans(sortedPlans);

      // Find the index of the current plan
      const planIndex = sortedPlans.findIndex(plan => plan.id === selectedPlan?.id);
      if (planIndex !== -1) {
        setCurrentPlanIndex(planIndex);
      }
    }
  }, [externalPlans, selectedPlan?.id, currentPlanId]);

  // Initialize currentPrice when selectedPrice changes
  useEffect(() => {
    if (selectedPrice) {
      setCurrentPrice(selectedPrice);
    }
  }, [selectedPrice]);

  const handlePeriodChange = (newPeriod) => {
    if (!currentPlan?.prices) return;

    const newPrice = currentPlan.prices.find(price =>
      price.period === newPeriod && price.active
    );

    if (newPrice) {
      setCurrentPrice(newPrice);
    }
  };

  const handlePlanChange = (direction) => {
    const newIndex = direction === 'next'
      ? (currentPlanIndex + 1) % plans.length
      : (currentPlanIndex - 1 + plans.length) % plans.length;

    const newPlan = plans[newIndex];
    if (newPlan && newPlan.prices && newPlan.prices.length > 0) {
      // Try to keep the current period, otherwise use the first available
      const currentPeriod = currentPrice?.period;
      const samePeriodPrice = newPlan.prices.find(p =>
        p.period === currentPeriod && p.active
      );
      const defaultPrice = samePeriodPrice || newPlan.prices.find(p => p.active) || newPlan.prices[0];

      setCurrentPlanIndex(newIndex);
      setCurrentPlan(newPlan);
      setCurrentPrice(defaultPrice);
    }
  };

  return {
    plans,
    currentPlan,
    currentPrice,
    handlePeriodChange,
    handlePlanChange
  };
};

// Memoized component for the Stripe form - defined outside the main component
const StripePaymentForm = memo(({ currentPrice, currentPlan, isLoading, setIsLoading, onSuccess, autoRenew, setAutoRenew }) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleStripeSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) { return; }

    setIsLoading(true);

    try {
      // Use a local state to not interfere with the main state
      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/companies/settings?tab=plan`,
        },
        redirect: "if_required",
      });

      if (error) {
        toast.error(error.message);
        return;
      }

      if (setupIntent?.status === "succeeded") {
        const subscriptionData = {
          data: {
            attributes: {
              payment_plan_price_id: currentPrice.id,
              stripe_payment_method_id: setupIntent.payment_method,
              auto_renew: autoRenew
            }
          }
        };

        try {
          const { data } = await api.post('/companies/subscriptions', subscriptionData);

          if (data?.isDowngrade) {
            toast.success(`Mudança de plano agendada! Você terá acesso ao ${currentPlan?.name || 'novo plano'} no fim do período atual.`, { autoClose: 8000 });
          } else {
            toast.success(`Plano alterado com sucesso para ${currentPlan?.name || 'o novo plano'}!`);
          }

          onSuccess();
        } catch (subscriptionError) {
          // Check if it's a billing data validation error (422 status code)
          if (subscriptionError.response?.status === 422 && subscriptionError.response?.data?.missing_fields) {
            const missingFields = subscriptionError.response.data.missing_fields;
            toast.error(
              `Complete os seguintes dados antes de assinar: ${missingFields.join(', ')}`,
              { autoClose: 8000 }
            );
            // Redirect to profile to complete the data
            setTimeout(() => {
              window.location.href = '/companies/settings?tab=profile';
            }, 2000);
          } else if (subscriptionError.response?.data?.errors?.[0]?.title) {
            toast.error(subscriptionError.response.data.errors[0].title);
          } else {
            toast.error("Ocorreu um erro ao ativar plano, por favor entre em contato com o suporte.");
          }
        }
      } else {
        toast.error("Ocorreu um erro ao processar o pagamento.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const paymentElementOptions = {
    layout: "tabs",
    business: { name: "eComLovers" },
  };

  return (
    <div className="checkoutSection">
      <div className="securityBadge">
        <img src={iconSegure} alt="Seguro" width={75} />
        <div className="securityText">
          <span className="securityTitle">
            Segurança em primeiro lugar!
          </span>
          <span className="securityDescription">
            Seu pagamento é processado com segurança pelo Stripe,
            protegido por criptografia SSL.
          </span>
        </div>
      </div>

      <form onSubmit={handleStripeSubmit} className="paymentForm">
        <PaymentElement
          id="payment-element"
          options={paymentElementOptions}
        />

        <div
          className={`buttonSavePaymentMethod ${isLoading ? 'loading' : ''}`}
          onClick={!isLoading && stripe && elements ? handleStripeSubmit : undefined}
        >
          {isLoading ? 'Processando...' : 'Contratar Plano'}
        </div>
      </form>
    </div>
  );
});

const PaymentCheckoutModal = ({ selectedPrice, selectedPlan, plans: externalPlans, currentPlanId, hasPaymentMethod, onSuccess, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [autoRenew, setAutoRenew] = useState(true);

  const {
    currentPlan,
    currentPrice,
    handlePeriodChange,
    handlePlanChange
  } = usePlanLogic(externalPlans, selectedPlan, selectedPrice, currentPlanId);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentPrice?.id) {
      toast.error("Por favor, selecione um período de pagamento.");
      return;
    }

    setIsLoading(true);

    try {
      const subscriptionData = {
        data: {
          attributes: {
            payment_plan_price_id: currentPrice.id,
            auto_renew: autoRenew
          }
        }
      };

      const { data } = await api.post('/companies/subscriptions', subscriptionData);

      if (data?.isDowngrade) {
        toast.success(`Mudança de plano agendada! Você terá acesso ao ${currentPlan?.name || 'novo plano'} no fim da assinatura atual.`, { autoClose: 8000 });
      } else {
        toast.success(`Plano alterado com sucesso para ${currentPlan?.name || 'o novo plano'}!`);
      }

      onSuccess();
    } catch (error) {
      // Check if it's a billing data validation error
      if (error.response?.status === 422 && error.response?.data?.missing_fields) {
        const missingFields = error.response.data.missing_fields;
        toast.error(
          `Complete os seguintes dados antes de assinar: ${missingFields.join(', ')}`,
          { autoClose: 8000 }
        );
        // Close modal and redirect to profile to complete data
        onCancel();
        // You could also trigger a navigation to the profile tab here
        setTimeout(() => {
          window.location.href = '/companies/settings?tab=profile';
        }, 2000);
      } else if (error.response?.data?.errors?.[0]?.title) {
        toast.error(error.response.data.errors[0].title);
      } else {
        toast.error("Ocorreu um erro ao alterar plano, por favor entre em contato com o suporte.");
      }
    } finally {
      setIsLoading(false);
    }
  };


  const modalContent = (
    <div className="paymentCheckoutModal">
      <div className="modalOverlay" onClick={!isLoading ? onCancel : undefined}></div>

      <div className="modalContent" style={{ width: hasPaymentMethod ? 'auto' : '100%' }}>
        {isLoading && (
          <div className="modalLoadingOverlay">
            <LoadingIcon />
          </div>
        )}

        <ModalHeader onClose={onCancel} isLoading={isLoading} />

        <div className="modalBody">
          <div className="contentWrapper" style={{ display: 'flex', flexDirection: hasPaymentMethod ? 'column' : 'row' }} >
            <div className="planNavigationContainer">
              <div
                className="navArrow navLeft"
                onClick={() => handlePlanChange('prev')}
                disabled={isLoading}
              >
                <img src={arrowLeft} alt="Arrow Left" width={16} height={16} />
              </div>

              <PlanSection
                currentPlan={currentPlan}
                currentPrice={currentPrice}
                onPeriodChange={handlePeriodChange}
                isLoading={isLoading}
                autoRenew={autoRenew}
                setAutoRenew={setAutoRenew}
              />

              <div className="navSpacer" />
              <div
                className="navArrow navRight"
                onClick={() => handlePlanChange('next')}
                disabled={isLoading}
              >
                <img src={arrowRight} alt="Arrow Right" width={16} height={16} />
              </div>
            </div>

            {!hasPaymentMethod && (
              <StripePaymentForm
                currentPrice={currentPrice}
                currentPlan={currentPlan}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                onSuccess={onSuccess}
                autoRenew={autoRenew}
                setAutoRenew={setAutoRenew}
              />
            )}

            {hasPaymentMethod && (
              <div className="modalActionsSingle">
                <div
                  className="buttonCancelModal"
                  onClick={!isLoading ? onCancel : undefined}
                >
                  Cancelar
                </div>
                <div
                  className={`buttonSavePaymentMethod ${isLoading ? 'loading' : ''}`}
                  onClick={!isLoading ? handleSubmit : undefined}
                >
                  {isLoading ? 'Processando...' : 'Contratar Plano'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Wrap with Stripe only if no payment method
  if (!hasPaymentMethod) {
    return ReactDOM.createPortal(
      <StripeWrapper plan={currentPlan?.id}>
        {modalContent}
      </StripeWrapper>,
      document.body
    );
  }

  return ReactDOM.createPortal(modalContent, document.body);
};

export default PaymentCheckoutModal;
